/* Kanva cart drawer — slide-in cart opened from the header bag icon.
   Built on Shopify's default cart: AJAX Cart API (/cart/change.js, /cart/update.js)
   plus the Section Rendering API to keep the drawer markup in sync with the server
   cart. Guarded: no-ops if the drawer section is absent. Reuses the add-to-cart
   flow in kanva.js via the 'kanva:cart-added' event.

   Beyond the line items the drawer carries four client-side pieces: a recently
   viewed tab fed from localStorage, a recommendations rail fetched from
   /recommendations/products, per-line gift wrapping written as a line item
   property, and the note / shipping / discount panels. */
(function () {
  'use strict';

  var RECENT_KEY = 'kanva:recently-viewed';
  var RECENT_MAX = 8;
  var strings = window.KanvaStrings || {};

  /* ----- Recently viewed: recorded on every product page, drawer or not ----- */
  function readRecent() {
    try {
      var list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return [];
    }
  }

  function currentProduct() {
    var node = document.querySelector('[data-kv-recent-product]');
    if (!node) return null;
    try {
      var data = JSON.parse(node.textContent);
      return data && data.handle ? data : null;
    } catch (error) {
      return null;
    }
  }

  var viewing = currentProduct();

  if (viewing) {
    var kept = readRecent().filter(function (item) {
      return item && item.handle !== viewing.handle;
    });
    kept.unshift(viewing);
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(kept.slice(0, RECENT_MAX)));
    } catch (error) {
      /* Private mode or a full quota: the tab just stays empty. */
    }
  }

  var drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  var panel = drawer.querySelector('[data-cart-panel]');
  var rootRoute = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var SECTION = 'cart-drawer';
  var RECO_SECTION = 'cart-recommendations';
  var lastTrigger = null;
  var busy = false;
  var openTool = null;
  var HOLD_KEY = 'kanva:cart-hold-until';
  var HOLD_MS = 5 * 60 * 1000;
  var holdTimer = null;

  function lockScroll(on) {
    document.body.classList.toggle('kv-cart-open', on);
  }

  function openDrawer(trigger) {
    lastTrigger = trigger || lastTrigger;
    var alreadyOpen = drawer.classList.contains('is-open') && !drawer.hidden;
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    lockScroll(true);
    setTab('cart');
    if (alreadyOpen) return;
    requestAnimationFrame(function () {
      drawer.classList.add('is-open');
      if (panel && typeof panel.focus === 'function') {
        panel.focus({ preventScroll: true });
      }
    });
    document.addEventListener('keydown', onKeydown);
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    lockScroll(false);
    document.removeEventListener('keydown', onKeydown);
    var finish = function () { drawer.hidden = true; };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else setTimeout(finish, 300);
    if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
    }
  }

  function setBusy(on) {
    busy = on;
    drawer.classList.toggle('is-loading', on);
  }

  function updateCounts(count) {
    if (count == null) return;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      if (el.hasAttribute('hidden') || el.classList.contains('kv-badge')) {
        el.hidden = count === 0;
      }
    });
  }

  /* ----- Tabs ----- */
  function setTab(name) {
    drawer.querySelectorAll('[data-cart-tab]').forEach(function (tab) {
      var on = tab.getAttribute('data-cart-tab') === name;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    drawer.querySelectorAll('[data-cart-view]').forEach(function (view) {
      var on = view.getAttribute('data-cart-view') === name;
      view.classList.toggle('is-active', on);
      view.hidden = !on;
    });
  }

  /* ----- Section rendering ----- */
  // Swap the drawer's inner contents (line items + footer) from freshly
  // rendered section HTML, and mirror any count found inside it.
  function applySectionHtml(html) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.querySelector('[data-cart-contents]');
    var current = drawer.querySelector('[data-cart-contents]');
    if (fresh && current) current.innerHTML = fresh.innerHTML;
    var freshCount = parsed.querySelector('[data-cart-count]');
    if (freshCount) updateCounts(parseInt(freshCount.textContent, 10) || 0);
    mount();
    // Only carts that actually contain a gift line pay for the extra
    // /cart.js round-trip the guard needs.
    if (drawer.querySelector('[data-cart-has-gift]')) enforceGiftRules();
  }

  // Pull the current cart drawer markup without mutating the cart.
  function refresh() {
    return fetch(rootRoute + '?sections=' + SECTION, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data[SECTION]) applySectionHtml(data[SECTION]);
      })
      .catch(function () {});
  }

  // Change a line's quantity (0 removes it) and re-render the drawer.
  function changeLine(key, quantity) {
    if (busy) return;
    setBusy(true);
    var payload = { id: key, quantity: quantity, sections: SECTION };
    fetch(rootRoute + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart && cart.sections && cart.sections[SECTION]) {
          applySectionHtml(cart.sections[SECTION]);
        } else {
          return refresh();
        }
        if (cart && typeof cart.item_count === 'number') updateCounts(cart.item_count);
      })
      .catch(function () {})
      .finally(function () { setBusy(false); });
  }

  /* ----- Free-gift guards ----- */
  // Real $0 gift lines are only legitimate while the qualifying bundle is in
  // the cart and priced at zero. Paid gift lines are converted into a display-
  // only cart-attribute banner (never charged). Preview attributes are cleared
  // when their qualifying variant leaves the cart.
  var giftGuardBusy = false;

  function formatWasMoney(cents) {
    if (!cents) return '';
    return '$' + (Number(cents) / 100).toFixed(2);
  }

  function clearGwpAttributes() {
    return fetch(rootRoute + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: { _gwp_for: '', _gwp_title: '', _gwp_image: '', _gwp_was: '' }
      })
    });
  }

  function setGwpAttributes(preview) {
    return fetch(rootRoute + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          _gwp_for: String(preview.for || ''),
          _gwp_title: preview.title || '',
          _gwp_image: preview.image || '',
          _gwp_was: preview.was || ''
        }
      })
    });
  }

  function enforceGiftRules() {
    if (giftGuardBusy) return;
    fetch(rootRoute + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return;

        var action = null;
        var preview = null;
        var attrs = cart.attributes || {};

        cart.items.some(function (line) {
          var qualifier = line.properties && line.properties._free_gift;
          if (!qualifier) return false;
          var qualifierId = parseInt(qualifier, 10);
          var stillQualified = cart.items.some(function (other) {
            return other !== line && other.variant_id === qualifierId;
          });
          if (!stillQualified) {
            action = { key: line.key, quantity: 0 };
          } else if (line.quantity > 1) {
            action = { key: line.key, quantity: 1 };
          } else if (line.final_line_price > 0) {
            /* Paid "gift" — remove the chargeable line and keep a preview banner. */
            action = { key: line.key, quantity: 0 };
            preview = {
              for: qualifier,
              title: line.product_title || line.title || '',
              image: line.image || '',
              was: formatWasMoney(line.original_line_price || line.final_line_price || line.price)
            };
          }
          return Boolean(action);
        });

        /* Preview attributes with no matching qualifier → clear them. */
        if (!action && attrs._gwp_for) {
          var forId = parseInt(attrs._gwp_for, 10);
          var still = cart.items.some(function (line) {
            return line.variant_id === forId;
          });
          if (!still) {
            giftGuardBusy = true;
            clearGwpAttributes()
              .then(function () {
                giftGuardBusy = false;
                return refresh();
              })
              .catch(function () { giftGuardBusy = false; });
            return;
          }
        }

        if (!action) return;

        giftGuardBusy = true;
        fetch(rootRoute + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: action.key, quantity: action.quantity }),
        })
          .then(function () {
            if (preview) return setGwpAttributes(preview);
          })
          .then(function () {
            giftGuardBusy = false;
            return refresh();
          })
          .catch(function () { giftGuardBusy = false; });
      })
      .catch(function () {});
  }

  /* ----- Recommendations rail ----- */
  function recoNav() {
    var mountPoint = drawer.querySelector('[data-cart-reco]');
    if (!mountPoint) return;
    var track = mountPoint.querySelector('[data-reco-track]');
    var prev = mountPoint.querySelector('[data-reco-prev]');
    var next = mountPoint.querySelector('[data-reco-next]');
    if (!track) return;
    var end = track.scrollWidth - track.clientWidth - 2;
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= end;
  }

  function loadRecommendations() {
    var mountPoint = drawer.querySelector('[data-cart-reco]');
    if (!mountPoint) return;
    var track = mountPoint.querySelector('[data-reco-track]');
    var productId = mountPoint.getAttribute('data-product-id');
    if (!track || !productId) return;

    var url = rootRoute + 'recommendations/products?section_id=' + RECO_SECTION +
      '&limit=6&product_id=' + encodeURIComponent(productId);

    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var cards = new DOMParser().parseFromString(html, 'text/html')
          .querySelectorAll('.kv-cart-reco__card');
        if (!cards.length) return;
        track.innerHTML = '';
        cards.forEach(function (card) { track.appendChild(card); });
        mountPoint.hidden = false;
        track.addEventListener('scroll', recoNav, { passive: true });
        recoNav();
      })
      .catch(function () {});
  }

  /* ----- Recently viewed rail ----- */
  // Mirrors {% render 'icon', name: 'plus' %} so these cards match the ones the
  // recommendations section renders server-side.
  var PLUS_ICON = '<svg class="kv-icon" viewBox="0 0 24 24" aria-hidden="true">' +
    '<path d="M12 5v14M5 12h14"/></svg>';

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (char) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
    });
  }

  function recentCard(item) {
    var url = escapeHtml(item.url);
    var image = item.image
      ? '<img src="' + escapeHtml(item.image) + '" alt="" loading="lazy" width="160" height="160">'
      : '';
    return '<article class="kv-cart-reco__card">' +
      '<a class="kv-cart-reco__media" href="' + url + '" tabindex="-1" aria-hidden="true">' + image + '</a>' +
      '<div class="kv-cart-reco__info">' +
        '<a class="kv-cart-reco__name" href="' + url + '">' + escapeHtml(item.title) + '</a>' +
        '<p class="kv-cart-reco__price">' + escapeHtml(item.price) + '</p>' +
      '</div>' +
      '<a class="kv-cart-reco__cta" href="' + url + '">' + PLUS_ICON +
        escapeHtml(strings.cartView || 'View') + '</a>' +
      '</article>';
  }

  function renderRecent() {
    var list = drawer.querySelector('[data-recent-list]');
    if (!list) return;
    var empty = drawer.querySelector('[data-recent-empty]');
    var tab = drawer.querySelector('[data-recent-tab]');
    var items = readRecent().filter(function (item) {
      return item && item.url && (!viewing || item.handle !== viewing.handle);
    });
    list.innerHTML = items.map(recentCard).join('');
    if (empty) empty.hidden = items.length > 0;
    if (tab) tab.hidden = items.length === 0;
  }

  /* ----- Note / shipping / discount panels ----- */
  function setTool(name) {
    openTool = name;
    drawer.querySelectorAll('[data-cart-tool]').forEach(function (button) {
      var on = button.getAttribute('data-cart-tool') === name;
      button.classList.toggle('is-open', on);
      button.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
    drawer.querySelectorAll('[data-cart-panel-for]').forEach(function (pane) {
      pane.hidden = pane.getAttribute('data-cart-panel-for') !== name;
    });
  }

  function showMessage(selector, text, isError) {
    var node = drawer.querySelector(selector);
    if (!node) return;
    node.textContent = text;
    node.classList.toggle('is-error', !!isError);
    node.hidden = !text;
  }

  function saveNote() {
    var field = drawer.querySelector('[data-cart-note]');
    if (!field) return;
    // The note changes nothing the footer renders, so this skips the re-render
    // and keeps the panel (and what was just typed) in place.
    fetch(rootRoute + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: field.value }),
    })
      .then(function () {
        showMessage('[data-cart-note-msg]', strings.cartNoteSaved || 'Note saved', false);
      })
      .catch(function () {
        showMessage('[data-cart-note-msg]', strings.cartNoteError || 'Could not save the note', true);
      });
  }

  function applyDiscount() {
    var field = drawer.querySelector('[data-cart-discount]');
    if (!field) return;
    var code = field.value.trim();
    if (!code) {
      showMessage('[data-cart-discount-msg]', strings.cartDiscountEmpty || 'Enter a code', true);
      return;
    }
    setBusy(true);
    // /discount/CODE is Shopify's own entry point: it validates the code and
    // stores it for checkout, after which the cart reports it back to us.
    fetch(rootRoute + 'discount/' + encodeURIComponent(code), { credentials: 'same-origin' })
      .then(function () {
        return fetch(rootRoute + 'cart.js', { headers: { Accept: 'application/json' } });
      })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var applied = (cart.discount_applications || []).some(function (discount) {
          return String(discount.title || '').toLowerCase() === code.toLowerCase();
        });
        return refresh().then(function () {
          setTool('discount');
          if (applied) {
            showMessage('[data-cart-discount-msg]', strings.cartDiscountApplied || 'Discount applied', false);
          } else {
            showMessage('[data-cart-discount-msg]', strings.cartDiscountFailed || 'That code is not valid', true);
          }
        });
      })
      .catch(function () {
        showMessage('[data-cart-discount-msg]', strings.cartDiscountFailed || 'That code is not valid', true);
      })
      .finally(function () { setBusy(false); });
  }

  function readHoldUntil() {
    try {
      var value = parseInt(window.sessionStorage.getItem(HOLD_KEY), 10);
      return value > 0 ? value : 0;
    } catch (error) {
      return 0;
    }
  }

  function writeHoldUntil(value) {
    try {
      window.sessionStorage.setItem(HOLD_KEY, String(value));
    } catch (error) {}
  }

  function clearHold() {
    try {
      window.sessionStorage.removeItem(HOLD_KEY);
    } catch (error) {}
    if (holdTimer) {
      clearInterval(holdTimer);
      holdTimer = null;
    }
  }

  function formatHold(ms) {
    var total = Math.max(0, Math.ceil(ms / 1000));
    var minutes = Math.floor(total / 60);
    var seconds = total % 60;
    return minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
  }

  function tickHold() {
    var banner = drawer.querySelector('[data-cart-hold]');
    var clock = drawer.querySelector('[data-cart-hold-time]');
    if (!banner || !clock) {
      clearHold();
      return;
    }
    var until = readHoldUntil();
    var now = Date.now();
    if (!until) {
      until = now + HOLD_MS;
      writeHoldUntil(until);
    }
    var left = until - now;
    clock.textContent = formatHold(left);
    banner.classList.toggle('is-ended', left <= 0);
    if (left <= 0 && holdTimer) {
      clearInterval(holdTimer);
      holdTimer = null;
    }
  }

  function startHold() {
    if (!drawer.querySelector('[data-cart-hold]')) {
      clearHold();
      return;
    }
    tickHold();
    if (!holdTimer) holdTimer = setInterval(tickHold, 1000);
  }

  /* Re-run every client-side piece that lives inside the swapped markup. */
  function mount() {
    loadRecommendations();
    if (openTool) setTool(openTool);
    startHold();
  }

  /* ----- Open from the header bag icon ----- */
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-cart-toggle]');
    if (!toggle) return;
    // Respect modified clicks so people can still open /cart in a new tab.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    openDrawer(toggle);
    refresh();
  });

  /* ----- Close: overlay, close buttons, in-drawer links ----- */
  drawer.addEventListener('click', function (event) {
    if (event.target.closest('[data-cart-close]')) {
      // Links inside the drawer (product, continue shopping) navigate normally;
      // only stop plain button closers.
      var link = event.target.closest('a[data-cart-close]');
      if (!link) event.preventDefault();
      closeDrawer();
    }
  });

  /* ----- Line controls, tabs, rails and panels (event delegation) ----- */
  drawer.addEventListener('click', function (event) {
    var tab = event.target.closest('[data-cart-tab]');
    if (tab) {
      setTab(tab.getAttribute('data-cart-tab'));
      return;
    }

    var tool = event.target.closest('[data-cart-tool]');
    if (tool) {
      var name = tool.getAttribute('data-cart-tool');
      setTool(openTool === name ? null : name);
      return;
    }

    if (event.target.closest('[data-cart-note-save]')) {
      saveNote();
      return;
    }

    if (event.target.closest('[data-cart-discount-apply]')) {
      applyDiscount();
      return;
    }

    var arrow = event.target.closest('[data-reco-prev], [data-reco-next]');
    if (arrow) {
      var track = drawer.querySelector('[data-reco-track]');
      if (track) {
        var step = arrow.hasAttribute('data-reco-next') ? track.clientWidth : -track.clientWidth;
        track.scrollBy({ left: step, behavior: 'smooth' });
      }
      return;
    }

    var minus = event.target.closest('[data-cart-minus]');
    var plus = event.target.closest('[data-cart-plus]');
    var remove = event.target.closest('[data-cart-remove]');

    if (minus) {
      var qMinus = (parseInt(minus.dataset.qty, 10) || 1) - 1;
      changeLine(minus.dataset.lineKey, Math.max(0, qMinus));
    } else if (plus) {
      var qPlus = (parseInt(plus.dataset.qty, 10) || 0) + 1;
      changeLine(plus.dataset.lineKey, qPlus);
    } else if (remove) {
      changeLine(remove.dataset.lineKey, 0);
    }
  });

  /* ----- Discount field: Enter applies ----- */
  drawer.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    if (!event.target.closest('[data-cart-discount]')) return;
    event.preventDefault();
    applyDiscount();
  });

  /* ----- After an add-to-cart (from any product form), open then refresh ----- */
  document.addEventListener('kanva:cart-added', function () {
    openDrawer(null);
    refresh();
  });

  // Recommendations wait for the first open: refresh() runs on every open and
  // ends in applySectionHtml, which mounts them.
  renderRecent();

    // The initial drawer markup is server-rendered, so a gift line can exist
  // before any AJAX render — e.g. after a full-page /cart quantity update.
  if (drawer.querySelector('[data-cart-has-gift]')) enforceGiftRules();
  startHold();
})();
